/**
 * Calls and bookings data slice: the calls answered for a store, the counters and reports built from them, and the store
 * card (what the assistant needs to know). The lists come from the booking-request contract
 * (shared/booking_request.mjs), the one definition the API validates against; this file takes only their types, so the
 * app's entry bundle carries none of the contract (the lists themselves are in ./lists.ts). Switch: `store_desk`.
 *
 * Real routes (api/src/modules/desk_log/routes.js, store_card/routes.js):
 *   GET /v1/app/desk-log/calls?from&to&department&kind&outcome&arrival&cursor&limit   leads.view      → {items, next, timezone}
 *   GET /v1/app/desk-log/calls/:id                                                    leads.view      → {entry, events}
 *   GET /v1/app/desk-log/counters?from&to                                             analytics.view
 *   GET /v1/app/desk-log/summary?date                                                 analytics.view
 *   GET /v1/app/desk-log/reports/weekly?week                                          analytics.view
 *   GET /v1/app/store-card                                 settings.manage | leads.view | analytics.view → {card}
 *   PUT /v1/app/store-card   (If-Match: <version>)         settings.manage → {card, changed}; 409 stale_version
 * The fixture implementation is in ./fixture.ts (dev / VITE_FIXTURES builds only).
 */
import type { ARRIVALS, DEPARTMENTS, KINDS, LANGUAGES, OUTCOMES, SOURCES, TRANSPORTS, VEHICLE_CONDITIONS } from '@shared/booking_request.mjs';
import { API_BASE, APP, type Http } from '@app/core/http';
import type { Cents, ISO, UUID } from '@app/core/types';

export type Source = (typeof SOURCES)[number];
export type Kind = (typeof KINDS)[number];
export type Department = (typeof DEPARTMENTS)[number];
export type Outcome = (typeof OUTCOMES)[number];
export type Transport = (typeof TRANSPORTS)[number];
export type Language = (typeof LANGUAGES)[number];
export type VehicleCondition = (typeof VEHICLE_CONDITIONS)[number];
/** A booked entry is `pending` until the arrival is checked against the store's scheduler. */
export type Arrival = 'pending' | (typeof ARRIVALS)[number];
/** Store-local calendar date, 'YYYY-MM-DD'. */
export type LocalDate = string;
export type EmailStatus = 'queued' | 'sent' | 'dry_run' | 'skipped' | 'failed';

/** One answered call, as recorded (desk_log service `shape`). Times are ISO instants; render them in the store's zone. */
export interface CallEntry {
  id: UUID; client_key: string; source: Source; kind: Kind; department: Department; outcome: Outcome;
  answered_at: ISO; language: Language | null;
  caller: { name: string | null; phone: string | null };
  vehicle: { year: number | null; make: string | null; model: string | null; mileage: number | null };
  concern: string | null; preferred_time: string | null; transport: Transport | null;
  appointment: { ref: string | null; at: ISO | null; confirmed_at: ISO | null };
  interest: { year: number | null; make: string | null; model: string | null; stock_number: string | null; condition: VehicleCondition | null; test_drive_at: ISO | null };
  notes: string | null;
  arrival: { status: Arrival | null; marked_at: ISO | null };
  handled_by: UUID | null;
  /** The store's own staff member who confirmed the request in the store's scheduler (first name and initial); null = not confirmed by a person. */
  confirmed_by_name?: string | null;
  /** The store's own follow-up of a sales call (the pipeline's "Contacted"). */
  contacted?: { at: ISO; by_name: string | null } | null;
  lead_email: { status: EmailStatus; sent_at: ISO | null; to?: string | null } | null;
  /** The call list only: the AI call behind this entry has a recording to play. */
  has_recording?: boolean;
  version: number; created_at: ISO; updated_at: ISO;
}
/** One row of an entry's history. `changes` maps a stored column to [before, after]. */
export interface CallEvent {
  id: string | number; event: string; actor_kind: string; actor_id: string | null; version: number;
  changes: Record<string, [unknown, unknown]>; created_at: ISO;
}
export interface CallDetail { entry: CallEntry; events: CallEvent[] }
export interface CallQuery { from?: LocalDate; to?: LocalDate; department?: Department; kind?: Kind; outcome?: Outcome; arrival?: Arrival; cursor?: string; limit?: number }
export interface CallPage { items: CallEntry[]; next: string | null; timezone: string }

export interface Counters {
  calls: number; web_requests: number; booked: number; booked_service: number; booked_sales: number;
  request_left: number; message: number; transferred: number; hung_up: number; wrong_number: number;
  service: number; sales: number; parts: number;
  arrived: number; arrived_service: number; no_show: number; arrival_pending: number;
  /** Service arrivals × the store's average repair order; null when the store has not set one. */
  revenue_cents: Cents | null;
  /** Calls answered while every department was closed (by the store card's hours). */
  after_hours?: number;
  /** Requests the store's own staff confirmed by name, and the median minutes from the call to that confirmation (null: none). */
  verified_by_person?: number;
  median_confirm_minutes?: number | null;
}
export interface CountersView { from: LocalDate; to: LocalDate; timezone: string; average_ro_cents: Cents | null; counters: Counters }
export interface DigestEmail { status: EmailStatus; sent_at: ISO | null; recipients: number }
export type SummaryCounts = Pick<Counters, 'calls' | 'web_requests' | 'booked' | 'request_left' | 'message' | 'transferred' | 'hung_up' | 'wrong_number'>;
export interface MorningSummary {
  date: LocalDate; timezone: string; opens_at: string | null; window: { start: ISO; end: ISO } | null;
  counts: SummaryCounts; calls: CallEntry[]; email: DigestEmail | null;
}
export interface WeeklyReport {
  week_start: LocalDate; week_end: LocalDate; timezone: string; average_ro_cents: Cents | null;
  this_week: Counters; previous_week: Counters; bookings: CallEntry[]; email: DigestEmail | null;
}

/** Opening windows in the store's own wall time ('HH:MM'; an end of '24:00' is midnight). */
export interface HoursWindow { start: string; end: string }
/** Keys are days of the week, '0' = Sunday. A missing day is closed. */
export type WeekHours = Partial<Record<'0' | '1' | '2' | '3' | '4' | '5' | '6', HoursWindow[]>>;
export interface CardDepartment { key: Department; hours: WeekHours; lead_email: string | null; notes: string | null; transfer_e164?: string | null }
export interface CardAdvisor { name: string; email: string | null; department: Department | null; receives_summary: boolean; receives_weekly: boolean }
export interface CardNotOffered { name: string; say_instead: string | null }
export interface StoreCard {
  store: { id: UUID; name: string; timezone: string; segment: string | null };
  /** 0 until the first save. */
  version: number;
  booking_url: string | null; average_ro_cents: Cents | null; agent_notes: string | null;
  departments: CardDepartment[]; advisors: CardAdvisor[]; not_offered: CardNotOffered[];
  updated_at: ISO | null;
  /** The assistant's settings (migration 276); absent from older payloads. */
  recording?: boolean; languages?: Language[]; after_hours_only?: boolean;
}
/** PUT body: the whole document, plus the version the edit was based on. */
export type StoreCardBody = Pick<StoreCard, 'booking_url' | 'average_ro_cents' | 'agent_notes' | 'departments' | 'advisors' | 'not_offered'> & { version: number };
export interface SaveResult { card: StoreCard; changed: boolean }

// ── the dealer workspace (switch dealer_workspace) ──────────────────────────────────────────────────────────────────
export type AppointmentStatus = 'upcoming' | 'awaiting_arrival' | 'arrived' | 'no_show';
export type AppointmentEntry = CallEntry & { status: AppointmentStatus };
/** PUT /v1/app/desk-log/calls/:id, the one change the store's staff make: a request becomes a confirmed appointment. */
export interface ConfirmBody { version: number; outcome: 'booked'; preferred_time: null; appointment: { ref: string; at: ISO } }
export interface AppointmentsQuery { from?: LocalDate; to?: LocalDate; kind?: 'service_appointment' | 'sales_appointment'; status?: AppointmentStatus }
export interface AppointmentsView { from: LocalDate; to: LocalDate; timezone: string; items: AppointmentEntry[] }
export type SalesStage = 'new' | 'contacted' | 'test_drive_booked' | 'showed';
export type PipelineEntry = CallEntry & { stage: SalesStage };
export interface PipelineView { from: LocalDate; to: LocalDate; timezone: string; stages: SalesStage[]; counts: Record<SalesStage, number>; items: PipelineEntry[] }
export interface CallerVehicle { year: number | null; make: string | null; model: string | null; mileage?: number | null; condition?: VehicleCondition | null }
export interface Caller {
  key: string; name: string | null; phone: string; calls: number; booked: number; arrived: number; sales_calls: number;
  first_call_at: ISO; last_call_at: ISO; next_appointment_at: ISO | null; vehicles: CallerVehicle[]; interest: CallerVehicle | null;
}
export interface CallersView { timezone: string; items: Caller[] }
export interface CallerDetail { timezone: string; caller: Caller; calls: CallEntry[] }
/** The AI's own record of a call it answered (voice module), linked to its call-log entry. */
export interface AiCallRef { id: UUID; has_recording: boolean; duration_sec: number | null; started_at: ISO; answered_at: ISO | null }
export interface AiTurn { turn_no: number; asr_text: string | null; bot_text: string | null; at: ISO | null }
export interface AiCallDetail { call: { id: UUID; started_at: ISO; answered_at: ISO | null; ended_at: ISO | null }; recordings: { leg: string; duration_ms: number | null }[]; turns?: AiTurn[] }
export interface PriceBookItem { id: UUID; name: string; category: string | null; kind: string; description: string | null; price_cents: Cents; default_minutes: number | null; active: boolean }

export interface Api {
  appointments(q: AppointmentsQuery): Promise<AppointmentsView>;
  pipeline(range: { from: LocalDate; to: LocalDate }): Promise<PipelineView>;
  callers(q: { q?: string }): Promise<CallersView>;
  caller(key: string): Promise<CallerDetail>;
  markContacted(id: string, contacted: boolean): Promise<{ entry: CallEntry; changed: boolean }>;
  /** The store's own staff confirm an appointment request in their scheduler: the reference and the time (PUT, with the version). */
  confirm(id: string, body: ConfirmBody): Promise<{ entry: CallEntry; changed: boolean }>;
  /** The store's own staff mark a confirmed appointment arrived or not (or back to pending). */
  markArrival(id: string, status: 'arrived' | 'no_show' | 'pending'): Promise<{ entry: CallEntry; changed: boolean }>;
  /** The AI call behind a call-log entry, or null when a person answered it (or none is linked). */
  aiCall(entryId: string): Promise<AiCallRef | null>;
  aiCallDetail(callId: string): Promise<AiCallDetail>;
  /** Where the browser plays a call's recording from (same origin, the session cookie). */
  recordingUrl(callId: string): string;
  priceBook(): Promise<PriceBookItem[]>;
  calls(q: CallQuery): Promise<CallPage>;
  call(id: string): Promise<CallDetail>;
  counters(range: { from: LocalDate; to: LocalDate }): Promise<CountersView>;
  summary(date: LocalDate): Promise<MorningSummary>;
  weekly(week: LocalDate): Promise<WeeklyReport>;
  card(): Promise<StoreCard>;
  saveCard(body: StoreCardBody): Promise<SaveResult>;
}

const LOG = `${APP}/desk-log`;
export const live = (http: Http): Api => ({
  calls: (q) => http.get<CallPage>(`${LOG}/calls`, { query: { ...q } }),
  call: (id) => http.get<CallDetail>(`${LOG}/calls/${encodeURIComponent(id)}`),
  counters: (range) => http.get<CountersView>(`${LOG}/counters`, { query: range }),
  summary: (date) => http.get<MorningSummary>(`${LOG}/summary`, { query: { date } }),
  weekly: (week) => http.get<WeeklyReport>(`${LOG}/reports/weekly`, { query: { week } }),
  card: () => http.get<{ card: StoreCard }>(`${APP}/store-card`).then((r) => r.card),
  saveCard: (body) => http.put<SaveResult>(`${APP}/store-card`, body, { version: body.version }),
  appointments: (q) => http.get<AppointmentsView>(`${LOG}/appointments`, { query: { ...q } }),
  pipeline: (range) => http.get<PipelineView>(`${LOG}/pipeline`, { query: range }),
  callers: (q) => http.get<CallersView>(`${LOG}/callers`, { query: q.q ? { q: q.q } : {} }),
  caller: (key) => http.get<CallerDetail>(`${LOG}/callers/${encodeURIComponent(key)}`),
  markContacted: (id, contacted) => http.post<{ entry: CallEntry; changed: boolean }>(`${LOG}/calls/${encodeURIComponent(id)}/contacted`, { contacted }),
  confirm: (id, body) => http.put<{ entry: CallEntry; changed: boolean }>(`${LOG}/calls/${encodeURIComponent(id)}`, body),
  markArrival: (id, status) => http.post<{ entry: CallEntry; changed: boolean }>(`${LOG}/calls/${encodeURIComponent(id)}/arrival`, { status }),
  aiCall: (entryId) => http.get<{ calls: AiCallRef[] }>(`${APP}/calls`, { query: { desk_log_entry_id: entryId, limit: 1 } }).then((r) => r.calls[0] ?? null),
  aiCallDetail: (callId) => http.get<AiCallDetail>(`${APP}/calls/${encodeURIComponent(callId)}`),
  recordingUrl: (callId) => `${API_BASE}${APP}/calls/${encodeURIComponent(callId)}/recording`,
  priceBook: () => http.get<{ items: PriceBookItem[] }>(`${APP}/catalog`, { query: { limit: 200 } }).then((r) => r.items),
});
